import { useEffect, useRef } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { announcementHtml, encodeAnnouncement } from "../utils/announcementFormatting";

export function AnnouncementText({ value }) {
  return <span dangerouslySetInnerHTML={{ __html: announcementHtml(value || "") }} />;
}

export default function AnnouncementRichText({ label, value, onChange, limit, greek, multiline = false, required = false }) {
  const editor = useRef(null);
  const selection = useRef(null);
  const emitted = useRef(value);
  useEffect(() => {
    if (editor.current && (value !== emitted.current || !editor.current.innerHTML)) {
      editor.current.innerHTML = announcementHtml(value);
      emitted.current = value;
    }
  }, [value]);
  const saveSelection = () => {
    const s = window.getSelection();
    if (s?.rangeCount && editor.current.contains(s.anchorNode) && editor.current.contains(s.focusNode)) selection.current = s.getRangeAt(0).cloneRange();
  };
  const emit = () => {
    emitted.current = encodeAnnouncement(editor.current.innerHTML);
    onChange(emitted.current);
    saveSelection();
  };
  const command = (name, arg) => {
    editor.current.focus();
    const s = window.getSelection();
    if (selection.current && editor.current.contains(selection.current.commonAncestorContainer)) {
      s.removeAllRanges(); s.addRange(selection.current);
    }
    document.execCommand("styleWithCSS", false, false);
    document.execCommand(name, false, arg);
    emit();
  };
  const tr = (el, en) => greek ? el : en;
  return <Box>
    <Typography variant="body2" sx={{ mb: .5 }}>{label}{required ? " *" : ""}</Typography>
    <Box sx={{ border: "1px solid", borderColor: value.length > limit ? "error.main" : "divider", borderRadius: 1, overflow: "hidden", "&:focus-within": { borderColor: "primary.main" } }}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={.5} sx={{ p: .5, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider" }} aria-label={tr("Μορφοποίηση", "Formatting")}>
        {[["bold", "B", tr("Έντονα", "Bold")], ["italic", "I", tr("Πλάγια", "Italic")], ["underline", "U", tr("Υπογράμμιση", "Underline")]].map(([cmd, text, title]) => <Button key={cmd} size="small" title={title} aria-label={title} onMouseDown={e => e.preventDefault()} onClick={() => command(cmd)} sx={{ minWidth: 30, fontWeight: cmd === "bold" ? 800 : 400, fontStyle: cmd === "italic" ? "italic" : "normal", textDecoration: cmd === "underline" ? "underline" : "none" }}>{text}</Button>)}
        <select aria-label={tr("Μέγεθος γραμμάτων", "Font size")} defaultValue="3" onChange={e => command("fontSize", e.target.value)}>
          <option value="2">{tr("Μικρό", "Small")}</option><option value="3">{tr("Κανονικό", "Normal")}</option><option value="4">{tr("Μεγάλο", "Large")}</option><option value="5">{tr("Πολύ μεγάλο", "Extra large")}</option>
        </select>
        <input type="color" defaultValue="#17324d" aria-label={tr("Χρώμα κειμένου", "Text color")} title={tr("Χρώμα κειμένου", "Text color")} onChange={e => command("foreColor", e.target.value)} style={{ width: 30, height: 26, border: 0, padding: 0 }} />
        <Button size="small" onMouseDown={e => e.preventDefault()} onClick={() => command("removeFormat")}>{tr("Καθαρισμός μορφοποίησης", "Clear formatting")}</Button>
      </Stack>
      <Box ref={editor} contentEditable suppressContentEditableWarning role="textbox" aria-label={label} aria-required={required} aria-multiline={multiline} onInput={emit} onKeyUp={saveSelection} onMouseUp={saveSelection} onBlur={saveSelection}
        onKeyDown={e => { if (!multiline && e.key === "Enter") e.preventDefault(); }}
        onPaste={e => { e.preventDefault(); command("insertText", e.clipboardData.getData("text/plain").replace(multiline ? /\r/g : /[\r\n]/g, "")); }}
        onDrop={e => e.preventDefault()}
        sx={{ minHeight: multiline ? 110 : 28, p: 1.25, outline: "none", whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 14 }} />
    </Box>
    <Typography variant="caption" color={value.length > limit ? "error" : "text.secondary"}>{value.trim().length}/{limit} · {tr("Το όριο περιλαμβάνει τη μορφοποίηση.", "The limit includes formatting.")}</Typography>
  </Box>;
}
